// UI-side metadata for the per-server module catalog. Lives in
// apps/web (not @os/contracts) because icons + Router URLs are UI
// concerns, not part of the api shape.
//
// Consumed by:
//   - PrimarySidebar.tsx (rail icons + navigation)
//   - components/admin/ServerModulesModal.tsx (toggle rows)
//
// Keys mirror MODULE_CATALOG in @os/contracts/shared/modules.

import {
  Clock, Mail, Calendar, Users, CheckSquare, Handshake,
  Headphones, Wallet, BookOpen, GraduationCap, Sparkles, Monitor, Shield, ShieldCheck, NotebookPen,
  LayoutDashboard, Bot, MonitorDot, HardDrive, ScanEye, FileSignature, Briefcase, TabletSmartphone,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ModuleKey } from '@os/contracts/shared'

interface ModuleUiMeta {
  icon: LucideIcon
  path: string
  match: string
}

// Desktop + Dashboards are special cases: not /workspace/... routes — clicking
// either sets the matching section in appShellStore and navigates to
// /channels/@me. The icon + label still drive the admin drawer; PrimarySidebar
// uses custom onClick handlers for them (see PrimarySidebar.tsx).
export const MODULE_UI_META: Record<ModuleKey, ModuleUiMeta> = {
  desktop:    { icon: Monitor,         path: '/channels/@me',        match: '/channels/@me' },
  dashboards: { icon: LayoutDashboard, path: '/channels/@me',        match: '/channels/@me' },
  tracker:   { icon: Clock,       path: '/tracker',             match: '/tracker' },
  mail:      { icon: Mail,        path: '/workspace/mail',      match: '/workspace/mail' },
  calendar:  { icon: Calendar,    path: '/workspace/calendar',  match: '/workspace/calendar' },
  people:    { icon: Users,       path: '/workspace/people',    match: '/workspace/people' },
  tasks:     { icon: CheckSquare, path: '/workspace/tasks',     match: '/workspace/tasks' },
  crm:       { icon: Handshake,   path: '/workspace/crm',       match: '/workspace/crm' },
  service:   { icon: Headphones,  path: '/workspace/service',   match: '/workspace/service' },
  hr:        { icon: Briefcase,   path: '/workspace/hr',        match: '/workspace/hr' },
  finance:   { icon: Wallet,      path: '/workspace/finance',   match: '/workspace/finance' },
  knowledge: { icon: BookOpen,       path: '/workspace/knowledge', match: '/workspace/knowledge' },
  notes:     { icon: NotebookPen,    path: '/workspace/notes',     match: '/workspace/notes' },
  sign:      { icon: FileSignature,  path: '/workspace/sign',      match: '/workspace/sign' },
  learning:  { icon: GraduationCap,  path: '/workspace/learning',  match: '/workspace/learning' },
  vault:     { icon: ShieldCheck,    path: '/workspace/vault',     match: '/workspace/vault' },
  // Probe = remote-support agents on client machines. Rail module at
  // /workspace/probe (device list + pairing code + live-screen Connect);
  // backend routes are requireAdmin-gated regardless of the rail entry.
  probe:     { icon: MonitorDot,     path: '/workspace/probe',     match: '/workspace/probe' },
  trust:     { icon: Shield,         path: '/workspace/trust',      match: '/workspace/trust' },
  mdm:       { icon: TabletSmartphone, path: '/workspace/mdm',      match: '/workspace/mdm' },
  files:     { icon: HardDrive,      path: '/workspace/files',     match: '/workspace/files' },
  ai:        { icon: Sparkles,    path: '/workspace/ai',        match: '/workspace/ai' },
  // AI Assistant is a footer button + right panel, not a rail nav entry
  // (rail:false in the catalog). path/match are non-matching sentinels —
  // the icon only drives the admin Modules toggle row.
  assistant: { icon: Bot,         path: '/__assistant__',       match: '/__assistant__' },
  // Monitoring = employee activity capture. Admin-only (rail:false): no rail
  // nav and no workspace page — surfaced only as a per-server admin section +
  // an admin Modules toggle row. path/match are non-matching sentinels.
  monitoring: { icon: ScanEye, path: '/__monitoring__', match: '/__monitoring__' },
}
